import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import Plot from "react-plotly.js";
import type { Shape } from "plotly.js";
import Tooltip from "@mui/material/Tooltip";
import { customInstance } from "../../../api/custom-instance";
import SelectField from "../../../components/Form/SelectFields";
import HorizontalNumberSlider from "../../../components/Data/HorizontalNumberSlider";
import MoleculeStructure from "../../../components/Data/MoleculeStructure";
import { TableData } from "../../../components/Table/DataTable";

interface ResultsetGroupingLike {
    modelFamilyName: string;
    modelHeaders: { label: string; value: string }[];
    dcmModelHashes: string[];
    data: TableData[];
}

type OverlayMode = "off" | "model-cv" | "resultset";

interface QuantileCurves {
    xOutPlot: number[];
    sigma: number;
    yOutProbMin: number[];
    yOutProbLow: number[];
    yOutProbExp: number[];
    yOutProbMid: number[];
    yOutProbHigh: number[];
    yOutProbMax: number[];
    yOutTrue?: number[];
    yOutProbBinary?: number[];
}

interface QuantileChannels {
    xDomain: number[];
    yProbMin: number[];
    yProbLow: number[];
    yProbExp: number[];
    yProbMid: number[];
    yProbHigh: number[];
    yProbMax: number[];
    yTrue?: number[];
    yProbBinary?: number[];
}

const DCM_SIGMA_MIN = 0.005;
const DCM_SIGMA_MAX = 0.15;

const toNumberOrNull = (value: unknown): number | null => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const toFiniteArray = (value: unknown): number[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return (value as unknown[])
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item));
};

const clampUnit = (value: number) => {
    return Math.max(0, Math.min(1, value));
};

const clampSigma = (value: number) => {
    if (!Number.isFinite(value)) {
        return 0.05;
    }
    return Math.min(Math.max(value, DCM_SIGMA_MIN), DCM_SIGMA_MAX);
};

const buildSmoothedQuantileCurves = ({
    xDomain,
    yProbMin,
    yProbLow,
    yProbExp,
    yProbMid,
    yProbHigh,
    yProbMax,
    yTrue,
    yProbBinary,
    sigmaOverride,
}: {
    xDomain: number[];
    yProbMin: number[];
    yProbLow: number[];
    yProbExp: number[];
    yProbMid: number[];
    yProbHigh: number[];
    yProbMax: number[];
    yTrue?: number[];
    yProbBinary?: number[];
    sigmaOverride?: number;
}): QuantileCurves | null => {
    const requiredLengths = [xDomain, yProbMin, yProbLow, yProbExp, yProbMid, yProbHigh, yProbMax].map((arr) => arr.length);
    const minLen = requiredLengths.length > 0 ? Math.min(...requiredLengths) : 0;

    if (minLen < 5 || requiredLengths.some((len) => len === 0)) {
        return null;
    }

    const xDomainTrimmed = xDomain.slice(0, minLen).map(clampUnit);
    const yProbMinTrimmed = yProbMin.slice(0, minLen).map(clampUnit);
    const yProbLowTrimmed = yProbLow.slice(0, minLen).map(clampUnit);
    const yProbExpTrimmed = yProbExp.slice(0, minLen).map(clampUnit);
    const yProbMidTrimmed = yProbMid.slice(0, minLen).map(clampUnit);
    const yProbHighTrimmed = yProbHigh.slice(0, minLen).map(clampUnit);
    const yProbMaxTrimmed = yProbMax.slice(0, minLen).map(clampUnit);
    const yTrueTrimmed = yTrue && yTrue.length >= minLen ? yTrue.slice(0, minLen).map(clampUnit) : undefined;
    const yProbBinaryTrimmed = yProbBinary && yProbBinary.length >= minLen ? yProbBinary.slice(0, minLen).map(clampUnit) : undefined;

    const numPointsPerLocalAverage = Math.min(75, 0.1 * xDomainTrimmed.length);
    const xSorted = [...xDomainTrimmed].sort((a, b) => a - b);
    const spacings = xSorted.slice(1).map((value, idx) => value - xSorted[idx]);
    const meanSpacing = spacings.length > 0
        ? spacings.reduce((sum, value) => sum + value, 0) / spacings.length
        : Number.NaN;

    let autoSigma = numPointsPerLocalAverage * meanSpacing;
    if (!Number.isFinite(autoSigma) || autoSigma <= 0) {
        autoSigma = numPointsPerLocalAverage;
    }

    autoSigma = clampSigma(autoSigma);
    const sigma = sigmaOverride !== undefined ? clampSigma(sigmaOverride) : autoSigma;

    const xMin = Math.min(...xDomainTrimmed);
    const xMax = Math.max(...xDomainTrimmed);
    let xOutMin = xMin + sigma;
    let xOutMax = xMax - sigma;
    let step = (xOutMax - xOutMin) / 500;

    if (!Number.isFinite(step) || step <= 0 || xOutMin >= xOutMax) {
        xOutMin = Math.max(0, xMin - 0.01);
        xOutMax = Math.min(1, xMax + 0.02);
        step = 0.01;
    }

    const xOutPlot: number[] = [];
    let current = xOutMin;
    while (current <= xOutMax && xOutPlot.length < 1200) {
        xOutPlot.push(clampUnit(current));
        current += step;
    }

    if (xOutPlot.length === 0) {
        return null;
    }

    const weightedMean = (yValues: number[]) => {
        return xOutPlot.map((xOut) => {
            let weightedSum = 0;
            let weightTotal = 0;

            for (let i = 0; i < xDomainTrimmed.length; i += 1) {
                const weight = Math.exp(-0.5 * Math.pow((xDomainTrimmed[i] - xOut) / sigma, 2));
                weightedSum += weight * yValues[i];
                weightTotal += weight;
            }

            return clampUnit(weightTotal > 0 ? weightedSum / weightTotal : 0);
        });
    };

    return {
        xOutPlot,
        sigma,
        yOutProbMin: weightedMean(yProbMinTrimmed),
        yOutProbLow: weightedMean(yProbLowTrimmed),
        yOutProbExp: weightedMean(yProbExpTrimmed),
        yOutProbMid: weightedMean(yProbMidTrimmed),
        yOutProbHigh: weightedMean(yProbHighTrimmed),
        yOutProbMax: weightedMean(yProbMaxTrimmed),
        yOutTrue: yTrueTrimmed ? weightedMean(yTrueTrimmed) : undefined,
        yOutProbBinary: yProbBinaryTrimmed ? weightedMean(yProbBinaryTrimmed) : undefined,
    };
};

const buildQuantileOverlayTraces = (curves: QuantileCurves, sourceLabel: string): any[] => {
    const traces: any[] = [
        {
            x: curves.xOutPlot,
            y: curves.yOutProbMin,
            mode: "lines",
            line: { width: 0, color: "rgba(0,0,0,0)" },
            name: `${sourceLabel} Quantiles`,
            showlegend: false,
            hoverinfo: "skip",
        },
        {
            x: curves.xOutPlot,
            y: curves.yOutProbLow,
            mode: "lines",
            fill: "tonexty",
            fillcolor: "rgba(248, 113, 113, 0.10)",
            line: { width: 0, color: "rgba(0,0,0,0)" },
            name: `${sourceLabel} q[min,low]`,
            showlegend: false,
            hoverinfo: "skip",
        },
        {
            x: curves.xOutPlot,
            y: curves.yOutProbMid,
            mode: "lines",
            fill: "tonexty",
            fillcolor: "rgba(251, 191, 36, 0.10)",
            line: { width: 0, color: "rgba(0,0,0,0)" },
            name: `${sourceLabel} q[low,mid]`,
            showlegend: false,
            hoverinfo: "skip",
        },
        {
            x: curves.xOutPlot,
            y: curves.yOutProbHigh,
            mode: "lines",
            fill: "tonexty",
            fillcolor: "rgba(147, 197, 253, 0.10)",
            line: { width: 0, color: "rgba(0,0,0,0)" },
            name: `${sourceLabel} q[mid,high]`,
            showlegend: false,
            hoverinfo: "skip",
        },
        {
            x: curves.xOutPlot,
            y: curves.yOutProbMax,
            mode: "lines",
            fill: "tonexty",
            fillcolor: "rgba(16, 185, 129, 0.10)",
            line: { width: 0, color: "rgba(0,0,0,0)" },
            name: `${sourceLabel} q[high,max]`,
            showlegend: false,
            hoverinfo: "skip",
        },
        {
            x: curves.xOutPlot,
            y: curves.yOutProbExp,
            mode: "lines",
            line: { color: "rgba(17, 24, 39, 0.35)", width: 2, dash: "dash" },
            name: `${sourceLabel} expected`,
            hoverinfo: "skip",
        },
    ];

    if (curves.yOutProbBinary) {
        traces.push({
            x: curves.xOutPlot,
            y: curves.yOutProbBinary,
            mode: "lines",
            line: { color: "rgba(59, 130, 246, 0.35)", width: 2, dash: "dot" },
            name: `${sourceLabel} binary`,
            hoverinfo: "skip",
        });
    }

    if (curves.yOutTrue) {
        traces.push({
            x: curves.xOutPlot,
            y: curves.yOutTrue,
            mode: "lines",
            line: { color: "rgba(107, 114, 128, 0.35)", width: 2 },
            name: `${sourceLabel} true`,
            hoverinfo: "skip",
        });
    }

    return traces;
};

const pickThreshold = (threshold: number): number => {
    if (!Number.isFinite(threshold)) return 0.5;
    return Math.max(0, Math.min(1, threshold));
};

const buildRowMaps = (grouping: ResultsetGroupingLike) => {
    return grouping.data.map((row) => {
        const fieldMap: { [key: string]: unknown } = {};
        row.fields.forEach((field) => {
            fieldMap[field.associatedColumn] = field.value;
        });
        return {
            id: row.id,
            values: fieldMap,
        };
    });
};

const chooseDomainColumn = (rowMaps: { id: string | number; values: { [key: string]: unknown } }[]) => {
    if (rowMaps.length === 0) return null;

    const allColumns = Object.keys(rowMaps[0].values);
    const prioritized = allColumns
        .filter((column) => !['structure/SMILES', 'smiles', 'is_duplicate'].includes(column))
        .map((column) => {
            const numericValues = rowMaps
                .map((row) => toNumberOrNull(row.values[column]))
                .filter((value): value is number => value !== null);

            const numericFraction = rowMaps.length > 0 ? numericValues.length / rowMaps.length : 0;
            return {
                column,
                numericFraction,
                isPreferred: /(y_prob_out_of_domain|out_of_domain|domain|ad-score|applicability)/i.test(column),
                values: numericValues,
            };
        })
        .filter((candidate) => candidate.numericFraction >= 0.7)
        .sort((a, b) => {
            if (a.isPreferred && !b.isPreferred) return -1;
            if (!a.isPreferred && b.isPreferred) return 1;
            return b.numericFraction - a.numericFraction;
        });

    return prioritized.length > 0 ? prioritized[0].column : null;
};

const chooseProbabilityColumn = (grouping: ResultsetGroupingLike, rowMaps: { id: string | number; values: { [key: string]: unknown } }[], selectedModelHash?: string | null) => {
    if (selectedModelHash) {
        return selectedModelHash;
    }

    if (grouping.dcmModelHashes.length > 0) {
        return grouping.dcmModelHashes[0];
    }

    // For non-DCM fallback, prefer a column with continuous (non-binary) values
    if (grouping.modelHeaders.length > 0) {
        // Check if the default header gives binary data; if so, look for a proba column
        const defaultCol = grouping.modelHeaders[0].value;
        const defaultValues = rowMaps
            .map((row) => toNumberOrNull(row.values[defaultCol]))
            .filter((v): v is number => v !== null);
        const isBinary = defaultValues.length > 0 && defaultValues.every(v => v === 0 || v === 1);

        if (isBinary) {
            // Try to find a proba-like hidden field
            const probaColumn = Object.keys(rowMaps[0]?.values ?? {}).find(
                (col) => /y_pred_proba/i.test(col)
            );
            if (probaColumn && hasUsableCoverage(rowMaps, probaColumn)) {
                return probaColumn;
            }
        }
        return defaultCol;
    }

    return null;
};

const hasUsableCoverage = (
    rowMaps: { id: string | number; values: { [key: string]: unknown } }[],
    column: string,
    minFraction = 0.7,
) => {
    if (rowMaps.length === 0) return false;

    const numericCount = rowMaps
        .map((row) => toNumberOrNull(row.values[column]))
        .filter((value): value is number => value !== null)
        .length;

    return numericCount / rowMaps.length >= minFraction;
};

const chooseExplicitDcmColumns = (
    rowMaps: { id: string | number; values: { [key: string]: unknown } }[],
    grouping: ResultsetGroupingLike,
    selectedModelHash?: string | null,
) => {
    const preferredModelHash = selectedModelHash
        ?? (grouping.dcmModelHashes.length > 0 ? grouping.dcmModelHashes[0] : null);

    const candidateHashes = preferredModelHash
        ? [preferredModelHash, ...grouping.dcmModelHashes.filter((hash) => hash !== preferredModelHash)]
        : [...grouping.dcmModelHashes];

    for (const hash of candidateHashes) {
        const domainColumn = `dcm/${hash}/y_prob_out_of_domain`;
        const probabilityColumn = `dcm/${hash}/y_prob_1_exp`;

        if (hasUsableCoverage(rowMaps, domainColumn) && hasUsableCoverage(rowMaps, probabilityColumn)) {
            return {
                domainColumn,
                probabilityColumn,
            };
        }
    }

    return null;
};

const buildDcmSeries = (grouping: ResultsetGroupingLike, threshold: number, selectedModelHash?: string | null) => {
    const rowMaps = buildRowMaps(grouping);
    const explicitColumns = chooseExplicitDcmColumns(rowMaps, grouping, selectedModelHash);

    // If no DCM models and no explicit DCM columns, show empty state
    if (!explicitColumns && grouping.dcmModelHashes.length === 0) {
        return {
            ready: false,
            reason: "Select a DCM model family to view trust analysis.",
        } as const;
    }

    const domainColumn = explicitColumns?.domainColumn ?? chooseDomainColumn(rowMaps);
    const probabilityColumn = explicitColumns?.probabilityColumn ?? chooseProbabilityColumn(grouping, rowMaps, selectedModelHash);

    if (!domainColumn || !probabilityColumn) {
        return {
            ready: false,
            reason: "No DCM-compatible domain/probability columns found.",
        } as const;
    }

    // Detect if domain column is an AD score (higher = in-domain) vs DCM out-of-domain (higher = OOD)
    const isAdScore = !explicitColumns && /(ad-score|applicability|tanimoto|similarity)/i.test(domainColumn)
        && !/(y_prob_out_of_domain|out_of_domain)/i.test(domainColumn);

    const thresholdValue = pickThreshold(threshold);

    const points = rowMaps
        .map((row) => {
            const rawDomainScore = toNumberOrNull(row.values[domainColumn]);
            const probability = toNumberOrNull(row.values[probabilityColumn]);
            const smilesValue = row.values['structure/SMILES'] ?? row.values['smiles'];
            const smiles = typeof smilesValue === 'string' ? smilesValue : '';
            if (rawDomainScore === null || probability === null) {
                return null;
            }

            // Invert AD scores so higher = more out-of-domain (consistent with DCM)
            const domainScore = isAdScore ? 1 - rawDomainScore : rawDomainScore;

            return {
                id: row.id,
                smiles,
                domainScore,
                probability,
                inDomain: domainScore <= thresholdValue,
            };
        })
        .filter((item): item is { id: string | number; smiles: string; domainScore: number; probability: number; inDomain: boolean } => item !== null);

    if (points.length === 0) {
        return {
            ready: false,
            reason: "No numeric DCM points were available for plotting.",
        } as const;
    }

    const inDomain = points.filter((point) => point.inDomain);
    const outDomain = points.filter((point) => !point.inDomain);

    return {
        ready: true,
        domainColumn,
        probabilityColumn,
        usingExplicitDcmChannels: Boolean(explicitColumns),
        isAdScoreInverted: isAdScore,
        points,
        inDomain,
        outDomain,
        thresholdValue,
    } as const;
};

export default function DCMTrustTab({
    grouping,
    threshold,
    setThreshold,
    selectedModelHash,
    setSelectedModelHash,
    modelHashLabels,
}: {
    grouping: ResultsetGroupingLike | null;
    threshold: number;
    setThreshold: Dispatch<SetStateAction<number>>;
    selectedModelHash?: string | null;
    setSelectedModelHash: Dispatch<SetStateAction<string | null>>;
    modelHashLabels: { [key: string]: string };
}) {
    const [mode, setMode] = useState<"aggregate" | "molecule">("molecule");
    const [overlayMode, setOverlayMode] = useState<OverlayMode>("off");
    const [dcmSigmaOverride, setDcmSigmaOverride] = useState<number | null>(null);
    const [selectedPointIndex, setSelectedPointIndex] = useState<number>(0);
    const [modelCvChannelsByHash, setModelCvChannelsByHash] = useState<{ [key: string]: QuantileChannels | null }>({});
    const [modelCvLoadingHash, setModelCvLoadingHash] = useState<string | null>(null);

    const modelOptionLabel = (modelHash: string) => {
        const method = modelHashLabels[modelHash];
        const shortHash = modelHash.slice(0, 8);
        if (method) {
            return `${method} (${shortHash})`;
        }
        return `Model (${shortHash})`;
    };

    const dcmSeries = useMemo(() => {
        if (!grouping) {
            return {
                ready: false,
                reason: "Select a model family to view DCM trust plots.",
            } as const;
        }
        return buildDcmSeries(grouping, threshold, selectedModelHash);
    }, [grouping, threshold, selectedModelHash]);

    const resultsetQuantileCurves = useMemo(() => {
        if (!grouping || !selectedModelHash) {
            return null;
        }

        const rowMaps = buildRowMaps(grouping);
        const channels: QuantileChannels = {
            xDomain: [],
            yProbMin: [],
            yProbLow: [],
            yProbExp: [],
            yProbMid: [],
            yProbHigh: [],
            yProbMax: [],
            yTrue: [],
            yProbBinary: [],
        };

        rowMaps.forEach((row) => {
            const x = toNumberOrNull(row.values[`dcm/${selectedModelHash}/y_prob_out_of_domain`]);
            const qMin = toNumberOrNull(row.values[`dcm/${selectedModelHash}/y_prob_1_min`]);
            const qLow = toNumberOrNull(row.values[`dcm/${selectedModelHash}/y_prob_1_low`]);
            const qExp = toNumberOrNull(row.values[`dcm/${selectedModelHash}/y_prob_1_exp`]);
            const qMid = toNumberOrNull(row.values[`dcm/${selectedModelHash}/y_prob_1_mid`]);
            const qHigh = toNumberOrNull(row.values[`dcm/${selectedModelHash}/y_prob_1_high`]);
            const qMax = toNumberOrNull(row.values[`dcm/${selectedModelHash}/y_prob_1_max`]);

            if (x === null || qMin === null || qLow === null || qExp === null || qMid === null || qHigh === null || qMax === null) {
                return;
            }

            channels.xDomain.push(x);
            channels.yProbMin.push(qMin);
            channels.yProbLow.push(qLow);
            channels.yProbExp.push(qExp);
            channels.yProbMid.push(qMid);
            channels.yProbHigh.push(qHigh);
            channels.yProbMax.push(qMax);

            const trueValue = toNumberOrNull(row.values[`dcm/${selectedModelHash}/y_true`]);
            if (trueValue !== null) {
                channels.yTrue?.push(trueValue);
            }

            const binaryValue = toNumberOrNull(row.values[`dcm/${selectedModelHash}/y_prob_binary`]);
            if (binaryValue !== null) {
                channels.yProbBinary?.push(binaryValue);
            }
        });

        return buildSmoothedQuantileCurves({
            ...channels,
            sigmaOverride: dcmSigmaOverride ?? undefined,
        });
    }, [grouping, selectedModelHash, dcmSigmaOverride]);

    useEffect(() => {
        if (!selectedModelHash) {
            return;
        }

        if (Object.prototype.hasOwnProperty.call(modelCvChannelsByHash, selectedModelHash)) {
            return;
        }

        let isCancelled = false;

        const fetchModelCvCurves = async () => {
            setModelCvLoadingHash(selectedModelHash);

            try {
                const modelResponse = await customInstance({
                    url: `model/?model_hash=${selectedModelHash}`,
                    method: "GET",
                });

                const model = modelResponse as any;
                const truePred = model?.model_metadata?.dcm_metadata?.submodel_nested_cv_stats?.cv_performance_df_dict?.true_pred ?? {};

                const channels: QuantileChannels = {
                    xDomain: toFiniteArray(truePred.y_prob_out_of_domain),
                    yProbMin: toFiniteArray(truePred.y_prob_1_min),
                    yProbLow: toFiniteArray(truePred.y_prob_1_low),
                    yProbExp: toFiniteArray(truePred.y_prob_1_exp),
                    yProbMid: toFiniteArray(truePred.y_prob_1_mid),
                    yProbHigh: toFiniteArray(truePred.y_prob_1_high),
                    yProbMax: toFiniteArray(truePred.y_prob_1_max),
                    yTrue: toFiniteArray(truePred.y_true),
                    yProbBinary: toFiniteArray(truePred.y_prob_binary),
                };

                if (!isCancelled) {
                    setModelCvChannelsByHash((previous) => ({
                        ...previous,
                        [selectedModelHash]: channels,
                    }));
                }
            } catch (error) {
                if (!isCancelled) {
                    setModelCvChannelsByHash((previous) => ({
                        ...previous,
                        [selectedModelHash]: null,
                    }));
                }
            } finally {
                if (!isCancelled) {
                    setModelCvLoadingHash((previous) => (previous === selectedModelHash ? null : previous));
                }
            }
        };

        fetchModelCvCurves();

        return () => {
            isCancelled = true;
        };
    }, [selectedModelHash, modelCvChannelsByHash]);

    const modelCvQuantileCurves = useMemo(() => {
        if (!selectedModelHash) {
            return null;
        }

        if (!Object.prototype.hasOwnProperty.call(modelCvChannelsByHash, selectedModelHash)) {
            return null;
        }

        const channels = modelCvChannelsByHash[selectedModelHash];
        if (!channels) {
            return null;
        }

        return buildSmoothedQuantileCurves({
            ...channels,
            sigmaOverride: dcmSigmaOverride ?? undefined,
        });
    }, [selectedModelHash, modelCvChannelsByHash, dcmSigmaOverride]);

    const selectedPoint = useMemo(() => {
        if (!dcmSeries.ready || dcmSeries.points.length === 0) return null;
        const safeIndex = Math.max(0, Math.min(selectedPointIndex, dcmSeries.points.length - 1));
        return dcmSeries.points[safeIndex];
    }, [dcmSeries, selectedPointIndex]);

    useEffect(() => {
        if (!dcmSeries.ready || dcmSeries.points.length === 0) {
            setSelectedPointIndex(0);
            return;
        }

        if (selectedPointIndex > dcmSeries.points.length - 1) {
            setSelectedPointIndex(0);
        }
    }, [dcmSeries, selectedPointIndex]);

    const activeOverlayCurves = useMemo(() => {
        if (overlayMode === "resultset") {
            return resultsetQuantileCurves;
        }

        if (overlayMode === "model-cv") {
            return modelCvQuantileCurves;
        }

        return null;
    }, [overlayMode, resultsetQuantileCurves, modelCvQuantileCurves]);

    const overlayWarning = useMemo(() => {
        if (overlayMode === "off") {
            return null;
        }

        const isModelCvLoading = overlayMode === "model-cv"
            && selectedModelHash
            && modelCvLoadingHash === selectedModelHash
            && !Object.prototype.hasOwnProperty.call(modelCvChannelsByHash, selectedModelHash);

        if (isModelCvLoading) {
            return "Loading Model CV quantile background...";
        }

        if (activeOverlayCurves) {
            return null;
        }

        return overlayMode === "model-cv"
            ? "Model CV quantile background is unavailable for the selected model."
            : "Resultset quantile background is unavailable for the selected model.";
    }, [overlayMode, activeOverlayCurves, selectedModelHash, modelCvLoadingHash, modelCvChannelsByHash]);

    const overlayTraces = useMemo(() => {
        if (!activeOverlayCurves || overlayMode === "off") {
            return [] as any[];
        }

        const sourceLabel = overlayMode === "model-cv" ? "Model CV" : "Resultset";
        return buildQuantileOverlayTraces(activeOverlayCurves, sourceLabel);
    }, [activeOverlayCurves, overlayMode]);

    const handleSigmaChange: Dispatch<SetStateAction<number>> = (nextSigma) => {
        const currentSigma = activeOverlayCurves?.sigma ?? dcmSigmaOverride ?? 0.05;
        const resolved = typeof nextSigma === "function"
            ? (nextSigma as (prev: number) => number)(currentSigma)
            : nextSigma;
        setDcmSigmaOverride(clampSigma(resolved));
    };

    const showSigmaControl = overlayMode !== "off" && Boolean(activeOverlayCurves);
    const sigmaValue = dcmSigmaOverride ?? activeOverlayCurves?.sigma ?? 0.05;
    const isSigmaManual = dcmSigmaOverride !== null;

    if (!dcmSeries.ready) {
        return (
            <div className="mt-10 w-full rounded-xl bg-white border border-outline p-6">
                <p className="text-default text-[18px] font-semibold">DCM Trust Analysis</p>
                <p className="text-default/70 mt-2">{dcmSeries.reason}</p>
            </div>
        );
    }

    const inDomainCount = dcmSeries.inDomain.length;
    const outDomainCount = dcmSeries.outDomain.length;
    const total = dcmSeries.points.length;
    const domainAxisLabel = dcmSeries.isAdScoreInverted ? "Out-of-Domain Score (inverted AD)" : "Domain Score";
    const probabilityAxisLabel = "Predicted Probability";
    const probabilityAxisShortLabel = "Predicted Probability";
    const domainScoreHelpText = dcmSeries.isAdScoreInverted
        ? `Out-of-Domain Score is derived from the applicability domain similarity (inverted: 1 - AD score). Score <= ${dcmSeries.thresholdValue.toFixed(3)} is In-Domain, score > ${dcmSeries.thresholdValue.toFixed(3)} is Out-of-Domain.`
        : `Domain Score indicates distance from the model domain. In this view, score <= ${dcmSeries.thresholdValue.toFixed(3)} is In-Domain and score > ${dcmSeries.thresholdValue.toFixed(3)} is Out-of-Domain. Default threshold is typically 0.5.`;
    const plotMargins = { l: 72, r: 24, b: 88, t: 60 };
    const horizontalLegendLayout = { orientation: "h" as const, x: 0, xanchor: "left" as const, y: -0.3, yanchor: "top" as const };
    const fixedHistogramBins = { start: 0, end: 1, size: 0.1 };

    const histogramShapes: Partial<Shape>[] = [
        {
            type: "line",
            x0: dcmSeries.thresholdValue,
            x1: dcmSeries.thresholdValue,
            y0: 0,
            y1: 1,
            yref: "paper",
            line: { color: "#ef4444", width: 2, dash: "dash" },
        },
    ];

    if (mode === "molecule" && selectedPoint) {
        histogramShapes.push({
            type: "line",
            x0: selectedPoint.domainScore,
            x1: selectedPoint.domainScore,
            y0: 0,
            y1: 1,
            yref: "paper",
            line: { color: "#2563eb", width: 2, dash: "solid" },
        });
    }

    const scatterShapes: Partial<Shape>[] = [
        {
            type: "line",
            x0: dcmSeries.thresholdValue,
            x1: dcmSeries.thresholdValue,
            y0: 0,
            y1: 1,
            yref: "paper",
            line: { color: "#ef4444", width: 2, dash: "dash" },
        },
    ];

    if (mode === "molecule" && selectedPoint) {
        scatterShapes.push({
            type: "line",
            x0: selectedPoint.domainScore,
            x1: selectedPoint.domainScore,
            y0: 0,
            y1: 1,
            yref: "paper",
            line: { color: "#2563eb", width: 2, dash: "solid" },
        });
    }

    const probabilitySplitPlot = (
        <Plot
            data={[
                {
                    type: "histogram",
                    x: dcmSeries.inDomain.map((point) => point.probability),
                    xbins: fixedHistogramBins,
                    marker: { color: "#16a34a" },
                    opacity: 0.55,
                    name: `In-Domain (n=${inDomainCount})`,
                },
                {
                    type: "histogram",
                    x: dcmSeries.outDomain.map((point) => point.probability),
                    xbins: fixedHistogramBins,
                    marker: { color: "#ef4444" },
                    opacity: 0.55,
                    name: `Out-of-Domain (n=${outDomainCount})`,
                },
                ...(mode === "molecule" && selectedPoint
                    ? [{
                        type: "scatter" as const,
                        mode: "lines" as const,
                        x: [selectedPoint.probability, selectedPoint.probability],
                        y: [0, 1],
                        line: { color: "#2563eb", width: 2 },
                        name: "Selected Molecule",
                    }]
                    : []),
            ] as any[]}
            layout={{
                title: { text: "Predicted Probability by In/Out Domain Split", font: { family: "Montserrat", size: 16 } },
                xaxis: { title: probabilityAxisLabel, range: [0, 1], fixedrange: true },
                yaxis: { title: "Count", fixedrange: true },
                barmode: "overlay",
                margin: plotMargins,
                autosize: true,
                legend: horizontalLegendLayout,
            }}
            config={{ displaylogo: false, responsive: true }}
            style={{ width: "100%", height: "360px" }}
        />
    );

    return (
        <div className="mt-10 w-full rounded-xl bg-white border border-outline p-6 flex flex-col gap-6">
            <div className="w-full grid grid-cols-1 md:grid-cols-[minmax(260px,460px)_auto] items-end gap-4">
                <div className="min-w-0 max-w-[460px] w-full">
                    {(grouping?.dcmModelHashes?.length ?? 0) > 0 && (
                        <SelectField
                            label="Model Method"
                            id="dcm-model-method"
                            name="dcm_model_method"
                            value={selectedModelHash ? { value: selectedModelHash, label: modelOptionLabel(selectedModelHash) } : null}
                            options={(grouping?.dcmModelHashes ?? []).map((hash) => ({
                                value: hash,
                                label: modelOptionLabel(hash),
                            }))}
                            isRequired={false}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={(e: any) => {
                                if (Array.isArray(e)) {
                                    return;
                                }
                                setSelectedModelHash(String(e?.value ?? "") || null);
                            }}
                            disabled={(grouping?.dcmModelHashes?.length ?? 0) <= 1}
                        />
                    )}
                </div>

                <div className="justify-self-start md:justify-self-end w-fit flex flex-wrap items-end gap-3 md:justify-end">
                    <div className="min-w-[280px]">
                        <HorizontalNumberSlider
                            label="Domain Threshold"
                            threshold={dcmSeries.thresholdValue}
                            setThreshold={setThreshold}
                            hasBorderAndPadding
                        />
                    </div>

                    <div className="w-fit">
                        <p className="text-[12px] font-medium text-default mb-2">View Mode</p>
                        <div className="inline-flex h-[44px] border border-outline rounded-lg p-1 items-center gap-1">
                            <button
                                onClick={() => setMode("aggregate")}
                                className={`px-3 h-full rounded text-[13px] transition-all ${mode === "aggregate" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                            >
                                Aggregate
                            </button>
                            <button
                                onClick={() => setMode("molecule")}
                                className={`px-3 h-full rounded text-[13px] transition-all ${mode === "molecule" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                            >
                                By Molecule
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-between items-start gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <p className="text-default text-[18px] font-semibold">
                            DCM Trust Analysis
                        </p>
                        <Tooltip
                            arrow
                            placement="right"
                            title={
                                <div className="max-w-[320px] text-[12px] leading-5">
                                    {domainScoreHelpText}
                                </div>
                            }
                        >
                            <button
                                type="button"
                                className="w-5 h-5 rounded-full border border-outline text-[12px] text-default/80 hover:bg-secondary transition-all"
                                aria-label="Domain Score explanation"
                            >
                                i
                            </button>
                        </Tooltip>
                    </div>
                    <p className="text-default/70 text-[14px] mt-1">
                        Model Family: {grouping?.modelFamilyName ?? "N/A"}
                    </p>
                    {selectedModelHash && (
                        <p className="text-default/70 text-[14px] mt-1">
                            Model: {modelOptionLabel(selectedModelHash)}
                        </p>
                    )}
                    {dcmSeries.usingExplicitDcmChannels && (
                        <p className="text-default/60 text-[12px] mt-1">
                            Using explicit DCM channels from prediction payload.
                        </p>
                    )}
                </div>
            </div>

            <div className="w-full rounded-lg bg-[#F7F9FC] border border-outline p-3 text-[13px] text-default flex gap-6">
                <span>Total: {total}</span>
                <span>In-Domain: {inDomainCount}</span>
                <span>Out-of-Domain: {outDomainCount}</span>
                <span>Threshold: {dcmSeries.thresholdValue.toFixed(3)}</span>
            </div>

            {overlayWarning && (
                <p className="text-[12px] text-warning">{overlayWarning}</p>
            )}

            {mode === "molecule" && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-0 items-stretch">
                    <div className="lg:col-span-4 border border-outline rounded-lg bg-[#FAFBFD] p-3 min-h-0 h-full flex flex-col">
                        <div className="flex-1 overflow-auto flex flex-col gap-3 pr-1">
                            {dcmSeries.points.map((point, index) => (
                                <button
                                    key={`${index}-${point.smiles || point.id}`}
                                    onClick={() => setSelectedPointIndex(index)}
                                    className={`w-full text-left border rounded-lg p-3 transition-all ${index === selectedPointIndex ? "border-primary bg-secondary" : "border-outline bg-white hover:border-primary"}`}
                                >
                                    <div className="flex justify-between items-center gap-2 mb-2">
                                        <p className="text-[12px] font-semibold text-default">Molecule #{index}</p>
                                    </div>
                                    <div className="w-full flex justify-center items-center">
                                        {point.smiles ? (
                                            <MoleculeStructure smiles={point.smiles} width={140} height={120} />
                                        ) : (
                                            <p className="text-default/70 text-[12px]">No SMILES available</p>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-8 flex flex-col gap-6">
                        {selectedPoint && (
                            <div className="rounded-xl border border-outline bg-white p-4">
                                <p className="font-semibold text-default mb-2">Selected Molecule Summary</p>
                                <div className="text-[13px] text-default flex flex-col gap-1">
                                    <p>Domain Score: {selectedPoint.domainScore.toFixed(4)}</p>
                                    <p>Predicted Probability: {selectedPoint.probability.toFixed(4)}</p>
                                    <p>Status: {selectedPoint.inDomain ? "In-Domain" : "Out-of-Domain"}</p>
                                </div>
                            </div>
                        )}

                        <div className="w-full flex flex-col gap-3 rounded-lg bg-white p-3">
                            <div className="w-full flex flex-wrap justify-between items-end gap-4">
                                <div className="w-fit">
                                    <p className="text-[12px] font-medium text-default mb-2">Quantile Background</p>
                                    <div className="inline-flex h-[44px] border border-outline rounded-lg p-1 items-center gap-1">
                                        <button
                                            onClick={() => setOverlayMode("off")}
                                            className={`px-3 h-full rounded text-[13px] transition-all ${overlayMode === "off" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                        >
                                            Off
                                        </button>
                                        <button
                                            onClick={() => setOverlayMode("model-cv")}
                                            className={`px-3 h-full rounded text-[13px] transition-all ${overlayMode === "model-cv" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                        >
                                            Model CV
                                        </button>
                                        <button
                                            onClick={() => setOverlayMode("resultset")}
                                            className={`px-3 h-full rounded text-[13px] transition-all ${overlayMode === "resultset" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                        >
                                            Resultset
                                        </button>
                                    </div>
                                </div>

                                {showSigmaControl && (
                                    <div className="flex items-end gap-3">
                                        <div className="min-w-[320px]">
                                            <HorizontalNumberSlider
                                                label="Sigma"
                                                threshold={sigmaValue}
                                                setThreshold={handleSigmaChange}
                                                hasBorderAndPadding
                                                range={{ min: DCM_SIGMA_MIN, max: DCM_SIGMA_MAX, step: 0.005, inputStep: 0.005 }}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setDcmSigmaOverride(null)}
                                            disabled={!isSigmaManual}
                                            className={`h-[44px] px-3 rounded border text-[13px] font-medium transition-all ${isSigmaManual ? "border-outline bg-white text-default hover:bg-secondary" : "border-outline/60 bg-white text-default/40 cursor-not-allowed"}`}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="min-w-[280px]">
                                <HorizontalNumberSlider
                                    label="Domain Threshold"
                                    threshold={dcmSeries.thresholdValue}
                                    setThreshold={setThreshold}
                                    hasBorderAndPadding
                                />
                            </div>
                        </div>

                        <Plot
                            data={[
                                ...overlayTraces,
                                {
                                    type: "scatter",
                                    mode: "markers",
                                    x: dcmSeries.inDomain.map((point) => point.domainScore),
                                    y: dcmSeries.inDomain.map((point) => point.probability),
                                    marker: { color: "#16a34a", size: 8, opacity: 0.7 },
                                    name: "In-Domain",
                                },
                                {
                                    type: "scatter",
                                    mode: "markers",
                                    x: dcmSeries.outDomain.map((point) => point.domainScore),
                                    y: dcmSeries.outDomain.map((point) => point.probability),
                                    marker: { color: "#ef4444", size: 8, opacity: 0.7 },
                                    name: "Out-of-Domain",
                                },
                                ...(selectedPoint
                                    ? [{
                                        type: "scatter" as const,
                                        mode: "markers" as const,
                                        x: [selectedPoint.domainScore],
                                        y: [selectedPoint.probability],
                                        marker: { color: "#2563eb", size: 13, symbol: "diamond" as const },
                                        name: "Selected Molecule",
                                    }]
                                    : []),
                            ] as any[]}
                            layout={{
                                title: { text: "Domain Score vs Predicted Probability", font: { family: "Montserrat", size: 16 } },
                                xaxis: { title: domainAxisLabel, range: [0, 1], fixedrange: true, automargin: true },
                                yaxis: { title: probabilityAxisShortLabel, range: [0, 1], fixedrange: true, automargin: true },
                                shapes: scatterShapes,
                                margin: plotMargins,
                                autosize: true,
                                legend: horizontalLegendLayout,
                            }}
                            config={{ displaylogo: false, responsive: true }}
                            style={{ width: "100%", height: "360px" }}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Plot
                                data={[
                                    {
                                        type: "histogram",
                                        x: dcmSeries.points.map((point) => point.domainScore),
                                        xbins: fixedHistogramBins,
                                        marker: { color: "#94a3b8" },
                                        opacity: 0.85,
                                        name: "Domain Score",
                                    },
                                ] as any[]}
                                layout={{
                                    title: { text: "Domain Score Distribution", font: { family: "Montserrat", size: 16 } },
                                    xaxis: { title: domainAxisLabel, range: [0, 1], fixedrange: true },
                                    yaxis: { title: "Count", fixedrange: true },
                                    shapes: histogramShapes,
                                    barmode: "overlay",
                                    margin: plotMargins,
                                    autosize: true,
                                }}
                                config={{ displaylogo: false, responsive: true }}
                                style={{ width: "100%", height: "360px" }}
                            />

                            {probabilitySplitPlot}
                        </div>
                    </div>
                </div>
            )}

            {mode === "aggregate" && (
                <>
                    <div className="w-full flex flex-col gap-3 rounded-lg bg-white p-3">
                        <div className="w-full flex flex-wrap justify-between items-end gap-4">
                            <div className="w-fit">
                                <p className="text-[12px] font-medium text-default mb-2">Quantile Background</p>
                                <div className="inline-flex h-[44px] border border-outline rounded-lg p-1 items-center gap-1">
                                    <button
                                        onClick={() => setOverlayMode("off")}
                                        className={`px-3 h-full rounded text-[13px] transition-all ${overlayMode === "off" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                    >
                                        Off
                                    </button>
                                    <button
                                        onClick={() => setOverlayMode("model-cv")}
                                        className={`px-3 h-full rounded text-[13px] transition-all ${overlayMode === "model-cv" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                    >
                                        Model CV
                                    </button>
                                    <button
                                        onClick={() => setOverlayMode("resultset")}
                                        className={`px-3 h-full rounded text-[13px] transition-all ${overlayMode === "resultset" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                    >
                                        Resultset
                                    </button>
                                </div>
                            </div>

                            {showSigmaControl && (
                                <div className="flex items-end gap-3">
                                    <div className="min-w-[320px]">
                                        <HorizontalNumberSlider
                                            label="Sigma"
                                            threshold={sigmaValue}
                                            setThreshold={handleSigmaChange}
                                            hasBorderAndPadding
                                            range={{ min: DCM_SIGMA_MIN, max: DCM_SIGMA_MAX, step: 0.005, inputStep: 0.005 }}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setDcmSigmaOverride(null)}
                                        disabled={!isSigmaManual}
                                        className={`h-[44px] px-3 rounded border text-[13px] font-medium transition-all ${isSigmaManual ? "border-outline bg-white text-default hover:bg-secondary" : "border-outline/60 bg-white text-default/40 cursor-not-allowed"}`}
                                    >
                                        Reset
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="min-w-[280px]">
                            <HorizontalNumberSlider
                                label="Domain Threshold"
                                threshold={dcmSeries.thresholdValue}
                                setThreshold={setThreshold}
                                hasBorderAndPadding
                            />
                        </div>
                    </div>

                    <Plot
                        data={[
                            ...overlayTraces,
                            {
                                type: "scatter",
                                mode: "markers",
                                x: dcmSeries.inDomain.map((point) => point.domainScore),
                                y: dcmSeries.inDomain.map((point) => point.probability),
                                marker: { color: "#16a34a", size: 8, opacity: 0.7 },
                                name: "In-Domain",
                            },
                            {
                                type: "scatter",
                                mode: "markers",
                                x: dcmSeries.outDomain.map((point) => point.domainScore),
                                y: dcmSeries.outDomain.map((point) => point.probability),
                                marker: { color: "#ef4444", size: 8, opacity: 0.7 },
                                name: "Out-of-Domain",
                            },
                        ] as any[]}
                        layout={{
                            title: { text: "Domain Score vs Predicted Probability", font: { family: "Montserrat", size: 16 } },
                            xaxis: { title: domainAxisLabel, range: [0, 1], fixedrange: true, automargin: true },
                            yaxis: { title: probabilityAxisShortLabel, range: [0, 1], fixedrange: true, automargin: true },
                            shapes: scatterShapes,
                            margin: plotMargins,
                            autosize: true,
                            legend: horizontalLegendLayout,
                        }}
                        config={{ displaylogo: false, responsive: true }}
                        style={{ width: "100%", height: "360px" }}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Plot
                            data={[
                                {
                                    type: "histogram",
                                    x: dcmSeries.points.map((point) => point.domainScore),
                                    xbins: fixedHistogramBins,
                                    marker: { color: "#94a3b8" },
                                    opacity: 0.85,
                                    name: "Domain Score",
                                },
                            ] as any[]}
                            layout={{
                                title: { text: "Domain Score Distribution", font: { family: "Montserrat", size: 16 } },
                                xaxis: { title: domainAxisLabel, range: [0, 1], fixedrange: true },
                                yaxis: { title: "Count", fixedrange: true },
                                shapes: histogramShapes,
                                barmode: "overlay",
                                margin: plotMargins,
                                autosize: true,
                            }}
                            config={{ displaylogo: false, responsive: true }}
                            style={{ width: "100%", height: "360px" }}
                        />

                        {probabilitySplitPlot}
                    </div>
                </>
            )}
        </div>
    );
}
